import { DataTableFilterParams, DataTablePFSEvent, DataTableSortMeta } from 'primereact/datatable'
import { FC, useContext, useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { toast } from 'react-toastify'

import dayjs from 'dayjs'
import { FormikProps, useFormik } from 'formik'
import { IncidentExportRequest } from 'src/modules/incidents/application/dto/IncidentExportRequest'
import { IncidentFindRequest } from 'src/modules/incidents/application/dto/IncidentFindRequest'
import { useIncidentExportQuery } from 'src/modules/incidents/application/IncidentExportQueries'
import { IncidentRow } from 'src/modules/incidents/domain/IncidentRow'
import { HttpIncidentRepository } from 'src/modules/incidents/infrastructure/HttpIncidentRepository'
import { formatDate } from 'src/utils/dateUtils'
import { AuthContext, Authority, PagingData, useInternalUser, useLang, useTranslator } from 'ufinet-web-functions'
import * as Yup from 'yup'

import { faDownload, faRefresh } from '@fortawesome/free-solid-svg-icons'

import {
	ClientSelectHandle,
	CorporateGroupSelectHandle,
	Table,
	TooltipButton,
	UfinetBox,
	UfinetButton,
	UfinetSectionBox,
} from 'ufinet-web-components'
import { ColumnsHiddenHandler } from './components/ColumnsHiddenHandler'
import { ListIncidentHeader } from './components/ListIncidentHeader'
import { incidentsColsData } from './IncidentColsData'
import { useListDataQueries } from './useListDataQueries'
import { useClientValidation } from './useClientValidation'
import { IIncidentsFilter } from 'src/interfaces/clientRepository/IClientRepository'
import { useIncidentsFilter } from 'src/hooks/useIncidentsFilter'

interface FormikValues extends DataTablePFSEvent {}

const ListIncidentsPage: FC<IIncidentsFilter> = () => {
	const translate = useTranslator()
	const authData = useContext(AuthContext)
	const isInternal = useInternalUser()
	const navigate = useNavigate()
	const lang = useLang()
	const roles = authData.userData?.authorities || []
	const corporatePermissions = Authority.getGtiCorporatePermissions(roles)
	const ftthPermissions = Authority.getGtiFtthPermissions(roles)
	const permissions = {
		corporate: corporatePermissions.canRead,
		ftth: ftthPermissions.canRead,
	}

	const MAX_RANGE_DAYS = 90;
	const { incidentsFilter, loading } = useIncidentsFilter()

	const [incidents, setIncidents] = useState<IncidentRow[]>([])
	const [loadingIncidents, setLoadingIncidents] = useState(false)
	const [visibleColumns, setVisibleColumns] = useState<{ [key: string]: boolean }>({
		tickerNumber: true,
		administrativeCode: true,
		title: true,
		country: true,
		customer: true,
		projectTypes: true,
		reportTypes: false,
		reportClassification: false,
		incidentStatues: true,
		degradationTypes: false,
		affectationTypes: false,
		reportOrigins: false,
		internalClientTicketNumber: true,
		serviceTypes: false,
		createdDate: true,
		solutionTypes: false,
		primaryCause: false,
		secondaryCause: false,
		imputableTo: false,
		serviceRestorationDate: false,
		statusCloseDate: false,
		totalImputableTime: false,
		rfoSentStatuses: false,
		rfoSentDate: false,
		descriptionDiscountTime: false,
		reportMassiveId: true,
	})

	const incidentRepository = useMemo(() => HttpIncidentRepository(authData), [authData])
	const { shouldProceedWithFindAll } = useClientValidation()
	const groupSelectRef = useRef<CorporateGroupSelectHandle>(null)
	const clientSelectRef = useRef<ClientSelectHandle>(null)

	const {
		affectationTypes,
		degradationTypes,
		processStatus,
		projectTypes,
		reportOrigins,
		reportTypes,
		rfoSentStatus,
		serviceTypes,
		solutionTypes,
		countries,
	} = useListDataQueries()

	const reportClassifications = useMemo(() => {
		const values = (incidents ?? [])
			.map((IncidentRow) => IncidentRow.reportClassification)
			.filter((value): value is string => typeof value === 'string' && value.trim() !== '')

		return Array.from(new Set(values)).map((value) => ({
			label: value,
			value,
		}))
	}, [incidents])

	const initialValues: FormikValues = useMemo(() => ({
		countryId: {},
		corporateGroupId: [],
		customersId: [],
		dateFrom: undefined as unknown as Date,
		dateTo: undefined as unknown as Date,
		first: 0,
		rows: 10,
		sortField: '',
		sortOrder: 1,
		multiSortMeta: [] as DataTableSortMeta[],
		filters: {} as DataTableFilterParams['filters'],
	}), [incidentsFilter, authData])

	const differBy90DaysOrMore = (dateFrom: Date, dateTo: Date) => {
		const differenceInMilliseconds = Math.abs(dateTo.getTime() - dateFrom.getTime())
		const millisecondsInOneDay = 24 * 60 * 60 * 1000
		const differenceInDays = differenceInMilliseconds / millisecondsInOneDay
		return differenceInDays <= 90
	}

	const [tablePFSEvent, setTablePFSEvent] = useState<DataTablePFSEvent>({} as DataTablePFSEvent)

	const defaultPaging: PagingData = useMemo(
		() => ({
			pageNumber: 0,
			pageSize: 10,
			totalElements: 0,
			totalPages: 1,
		}),
		[]
	)

	const [paging, setPaging] = useState<PagingData>(defaultPaging)

	const validationSchema = Yup.object().shape({
		dateFrom: Yup.date()
			.optional()
			.test('differBy90DaysOrMore', 'Error', function () {
				const { dateFrom, dateTo } = this.parent
				if (dateFrom && dateTo) {
					return differBy90DaysOrMore(new Date(dateFrom), new Date(dateTo))
				} else if (dateFrom) {
					return differBy90DaysOrMore(new Date(dateFrom), new Date())
				} else {
					return true
				}
			}),
		dateTo: Yup.date()
			.optional()
			.test('differBy90DaysOrMore', 'Error', function () {
				const { dateFrom, dateTo } = this.parent
				if (dateFrom && dateTo) {
					return differBy90DaysOrMore(new Date(dateFrom), new Date(dateTo))
				} else if (dateFrom) {
					return differBy90DaysOrMore(new Date(dateFrom), new Date())
				} else {
					return true
				}
			}),
	})

	const findTickets = async (event: DataTablePFSEvent) => {
		setLoadingIncidents(true)
		formikSearch.values.customersId = formik.values.customersId.length < 1 ? incidentsFilter?.customerIds : formik.values?.customersId
		formikSearch.values.corporateGroupId = formik.values.corporateGroupId.length < 1 ? incidentsFilter?.corporateGroupIdsList : formik.values?.corporateGroupId
		formikSearch.values.countryId = formik.values.countryId
		formikSearch.values.dateTo = formik.values.dateTo
		formikSearch.values.dateFrom = formik.values.dateFrom
		
		if(!loading){
			try {
				const isValid = await shouldProceedWithFindAll(isInternal, formikSearch.values.customersId)				
				if (!isValid) {
					setIncidents([])
					setPaging(defaultPaging)
					setLoadingIncidents(false)
					return
				}
				
				const data = await incidentRepository
					.findAll(IncidentFindRequest.fromValues(isInternal, undefined, lang, formikSearch.values, event, permissions))
				
				setIncidents(data.incidents ?? [])
				setPaging({
					pageNumber: data.pageNumber,
					pageSize: data.rowsPerPage,
					totalPages: data.totalPages,
					totalElements: data.totalRows,
				})
				setLoadingIncidents(false)
			} catch (error) {
				toast.error(translate('ERROR.TICKET.FILTER'))
				setLoadingIncidents(false)
			}
		}
	}

	const findTicketsInternal = async (event: DataTablePFSEvent) => {
		setLoadingIncidents(true)
		
		if(!loading){
			try {
				// Validar cliente antes de hacer la búsqueda (para usuarios internos siempre retorna true)
				const isValid = await shouldProceedWithFindAll(isInternal, formik.values.customersId)
				
				if (!isValid) {					
					setIncidents([])
					setPaging(defaultPaging)
					setLoadingIncidents(false)
					return
				}
				
				const data = await incidentRepository
					.findAll(IncidentFindRequest.fromValues(isInternal, undefined, lang, formik.values, event, permissions))
				
				setIncidents(data.incidents ?? [])
				setPaging({
					pageNumber: data.pageNumber,
					pageSize: data.rowsPerPage,
					totalPages: data.totalPages,
					totalElements: data.totalRows,
				})
				setLoadingIncidents(false)
			} catch (error) {
				toast.error(translate('ERROR.TICKET.FILTER'))
				setLoadingIncidents(false)
			}
		}
	}

	const formik: FormikProps<FormikValues> = useFormik({
		initialValues: initialValues,
		validationSchema: validationSchema,
		onSubmit: isInternal ? findTicketsInternal : findTickets,
		validateOnChange: false,
		validateOnBlur: false,
		enableReinitialize: true,
	})

	const formikSearch: FormikProps<FormikValues> = useFormik({
		initialValues: initialValues,
		validationSchema: validationSchema,
		onSubmit: isInternal ? findTicketsInternal : findTickets,
		validateOnChange: false,
		validateOnBlur: false,
		enableReinitialize: true,
	})

	useEffect(() => {
		if(!loading)
			isInternal ? findTicketsInternal(tablePFSEvent) : findTickets(tablePFSEvent)
	}, [loading])

	const { mutate: exportIncidents, isLoading: isExporting } = useIncidentExportQuery(incidentRepository, {
		onSuccess: (response) => {
			response.blob().then((data) => {
				const url = window.URL.createObjectURL(new Blob([data]))
				const link = document.createElement('a')
				link.href = url
				link.download = `Incidents_Export_From_${formatDate(formik.values.dateFrom, '-', false)}_To_${formatDate(formik.values.dateTo, '-', false)}.xlsx`
				document.body.appendChild(link)
				link.click()
				document.body.removeChild(link)
				toast.success(translate('TICKET.EXPORT.SUCCESS'))
			})
		},
		onError: () => toast.error(translate('TICKET.EXPORT.ERROR')),
	})

	const handleExportIncidents = () => {
		const {
			customersId: rawCustomersId,
			corporateGroupId: rawCorporateGroupId,
			countryId,
			dateFrom,
			dateTo,
		} = formik.values;

		if (!dateFrom) {
			toast.error(translate('TICKET.EXPORT.SELECT_DATE_FROM'));
			return;
		}

		const from = dayjs(dateFrom)
  		const to   = dateTo ? dayjs(dateTo) : dayjs();
		
		const exportFilters = {
			customersId:
			rawCustomersId.length > 0
			? rawCustomersId
			: incidentsFilter?.customerIds,
			corporateGroupId:
			rawCorporateGroupId.length > 0
			? rawCorporateGroupId
			: incidentsFilter?.corporateGroupIdsList,
			countryId,
			dateFrom: from.toDate(),
			dateTo: to.toDate()
		}

		if (to.diff(from, 'day') > MAX_RANGE_DAYS) {
			toast.error(translate('TICKET.EXPORT.DATE_RANGE'));
			return;
		}

		exportIncidents(
			IncidentExportRequest.fromValues(
				isInternal,
				undefined,
				lang,
				exportFilters,
				tablePFSEvent,
				permissions
			)
		)
	}

	const navigateTo = (row: IncidentRow) => {
		navigate(
			row?.projectType?.includes('FTTH')
				? `/ftth-tickets-detail/${row.incidentId}`
				: `/corporate-tickets-detail/${row.incidentId}`
		)
	}

	return (
		<UfinetBox>
			<form onSubmit={formik.handleSubmit}>
				<ListIncidentHeader
					formik={formik}
					clientSelectRef={clientSelectRef}
					groupSelectRef={groupSelectRef}
					findTickets={findTickets}
					findTicketsInternal={findTicketsInternal}
					isInternal={isInternal}
					tablePFSEvent={tablePFSEvent}
				/>
				<UfinetSectionBox className="mt-3">
					<Table
						cols={incidentsColsData(
							translate,
							navigateTo,
							visibleColumns,
							reportTypes,
							degradationTypes,
							affectationTypes,
							projectTypes,
							processStatus,
							reportOrigins,							
							rfoSentStatus,
							reportClassifications,
							serviceTypes,
							solutionTypes,
							countries,
						)}
						content={incidents}
						editDisabled={true}
						onFilterClear={() => {
							formik.resetForm()
							isInternal ? findTicketsInternal({} as DataTablePFSEvent) : findTickets({} as DataTablePFSEvent)
						}}
						headerButtons={
							<div className="d-flex flex-row gap-3">
								<ColumnsHiddenHandler visibleColumns={visibleColumns} setVisibleColumns={setVisibleColumns} />
								<UfinetButton
									icon={faRefresh}
									type="button"
									id="refresh-button"
									onClick={() => isInternal ? findTicketsInternal(tablePFSEvent) : findTickets(tablePFSEvent)}
								/>
								{(corporatePermissions.canExport || ftthPermissions.canExport) &&
									(isExporting ? (
										<TooltipButton
											active
											behaviour="hover"
											children={translate('TICKET.EXPORT.DOWNLOADING')}
											buttonProps={{
												isDisabled: isExporting,
												icon: faDownload,
											}}
											tooltipProps={{
												placement: 'bottom',
												showArrow: true,
											}}
										/>
									) : (
										<UfinetButton icon={faDownload} isDisabled={isExporting} onClick={() => handleExportIncidents()} />
									))}
							</div>
						}
						afterTablePFSEvent={setTablePFSEvent}
						lazySettings={
							paging && {
								...paging,
								loading: loadingIncidents,
								onPage: (event: DataTablePFSEvent) => isInternal ? findTicketsInternal(event) : findTickets(event),
								onFilter: (event: DataTablePFSEvent) => isInternal ? findTicketsInternal(event) : findTickets(event),
								onSort: (event: DataTablePFSEvent) => isInternal ? findTicketsInternal(event) : findTickets(event),
							}
						}
					/>
				</UfinetSectionBox>
			</form>
		</UfinetBox>
	)
}

export { ListIncidentsPage }
